import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { SimpleTable } from '@tassat/ag-grid';
import {
    AlertTypes,
    FeeTypeEnum,
    FeeTypeMap,
    orderSideTypeCharMap
} from '@tassat/common-definitions';
import { StyledSelect } from '@tassat/formik-input';
import { FeeRecord } from '@tassat/fred-definitions';
import { AssetTicker } from '@tassat/fred-extended-definitions';
import { AlertModal } from '@tassat/modal';
import { convertToNumber, formatAsAsset } from '@tassat/utility';
import React from 'react';
import { createFilter } from 'react-select';

import { useAppSelector } from 'Reducers/store/hooks';
import { getAssetFromTicker } from 'Selectors/assets';
import { getFees } from 'Selectors/common';
import { getFirmLiquidityFacilityStats } from 'Selectors/firmStats';
import LiquidityFacilityStandingCellRenderer from 'SharedComponents/cellRenderComponent/lfStandingCellRenderer';

import { columnDefs } from './liquidityFacilityOverviewGridOptions';
import { muiStyles } from './styles';

export const LiquidityFacilityOverviewTable = () => {
    const dxyAsset = useAppSelector((state) => getAssetFromTicker(state, AssetTicker.USD));
    const fees = useAppSelector(getFees);
    const lfStats = useAppSelector(getFirmLiquidityFacilityStats);
    const classes = muiStyles();
    const [ feeModalOpen, setFeeModalOpen ] = React.useState<boolean>(false);
    const [ selectedLfFirm, setSelectedLfFirm ] = React.useState<any>(lfStats[0]);

    const FirmSelector = () => {
        const lfFirmOptions = lfStats.map((lfStat) => ({
            label: lfStat.firm.Name,
            value: lfStat
        }));

        const selectStyles = {
            control: (base) => ({
                ...base,
                minHeight: '18px',
                height: '24px'
            }),
            valueContainer: (base) => ({
                ...base,
                height: '24px',
                padding: '0 8px'
            }),
            singleValue: (base) => ({
                ...base,
                height: '24px'
            }),
            input: (base) => ({
                ...base,
                height: '24px',
                margin: '0 2px',
                padding: 0
            }),
            indicatorsContainer: (base) => ({
                ...base,
                height: '24px'
            }),
            dropdownIndicator: (base) => ({
                ...base,
                padding: '2px',
                height: '24px'
            }),
            indicatorSeparator: (base) => ({
                ...base,
                display: 'none' as 'none'
            })
        };

        return (
            <StyledSelect
                className={classes.lfSelectContainer}
                customStyles={selectStyles}
                filterOption={createFilter({ matchFrom: 'any' })}
                id='liquidityFacilitySelect'
                onChange={(option) => setSelectedLfFirm(option?.value)}
                options={lfFirmOptions}
                value={lfFirmOptions.find((lfFirm) => selectedLfFirm.firm.FirmId === lfFirm.value.firm.FirmId)}
            />
        );
    };

    const FeeDisplay = () => {
        const feeValueDisplay = (fee: FeeRecord) => {
            if (fee.FeeType === FeeTypeEnum.FIXED_AMOUNT) {
                return `${formatAsAsset(fee.FeeValue, dxyAsset, 'symbol')}`;
            }

            if (fee.FeeType === FeeTypeEnum.PERCENTAGE) {
                return `${fee.FeeValue}%`;
            }

            if (fee.FeeType === FeeTypeEnum.BASIS_POINTS) {
                return `${convertToNumber(fee.FeeValue) / 100}%`;
            }

            return fee.FeeValue;
        };

        return (
            <Grid
                container
                direction='column'
            >
                <Grid
                    container
                    direction='row'
                    justifyContent='space-between'
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.noBorderCell}>
                                    <Typography className={classes.feeDetailsHeader}>Order Type</Typography>
                                </TableCell>
                                <TableCell className={classes.noBorderCell}>
                                    <Typography className={classes.feeDetailsHeader}>Fee Type</Typography>
                                </TableCell>
                                <TableCell className={classes.noBorderCell}>
                                    <Typography className={classes.feeDetailsHeader}>Fee</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.values(fees)
                                .map((fee) => (
                                    <TableRow
                                        key={fee.FeeApply}
                                    >
                                        <TableCell className={classes.noBorderCell}>
                                            <Typography className={classes.feeType}>{orderSideTypeCharMap(fee.FeeApply)} Order</Typography>
                                        </TableCell>
                                        <TableCell className={classes.noBorderCell}>
                                            <Typography>{FeeTypeMap[fee.FeeType]?.displayValue || 'UNK'}</Typography>
                                        </TableCell>
                                        <TableCell className={classes.noBorderCell}>
                                            <Typography>
                                                {feeValueDisplay(fee)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            <SimpleTable
                columnDefs={columnDefs}
                components={{ standing: LiquidityFacilityStandingCellRenderer }}
                getRowId={({ data }) => data.stats.AssetId}
                minHeight={120}
                overlayNoRowsTemplate='No Liquidity Facility Data'
                rowData={selectedLfFirm?.statsWithAsset || []}
                Subtitle={FirmSelector}
                title='Liquidity Facility'
            />
            <div className={classes.seeDetailsButtonContainer}>
                <Typography
                    className={classes.seeDetailsButton}
                    onClick={() => setFeeModalOpen(true)}
                >
                    View Fee Structure
                </Typography>
            </div>
            <AlertModal
                Body={<FeeDisplay />}
                onClose={() => setFeeModalOpen(false)}
                open={feeModalOpen}
                title='Fee Structure'
                type={AlertTypes.message}
            />
        </>
    );
};